import { useState, useEffect } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import BoxForm from '../components/BoxForm';
import PageHeader from '../components/ui/PageHeader';

export default function Boxes() {
  const [boxes, setBoxes] = useState([]);
  const [filteredBoxes, setFilteredBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingBox, setEditingBox] = useState(null);
  const [filters, setFilters] = useState({
    search: ''
  });

  useEffect(() => {
    fetchBoxes();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxes, filters]);

  const fetchBoxes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/boxes');
      setBoxes(response.data.data);
    } catch (err) {
      setError('Failed to fetch boxes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...boxes];

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(b =>
        b.name?.toLowerCase().includes(search) ||
        b.key?.toLowerCase().includes(search)
      );
    }

    // Sort by displayOrder for proper ordering
    filtered.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

    setFilteredBoxes(filtered);
  };

  const handleAdd = () => {
    setEditingBox(null);
    setShowForm(true);
  };

  const handleEdit = (box) => {
    setEditingBox(box);
    setShowForm(true);
  };

  const handleDelete = async (box) => {
    if (!window.confirm(`Are you sure you want to delete "${box.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/auth/boxes/${box._id}`);
      fetchBoxes();
    } catch (err) {
      alert('Failed to delete box');
      console.error(err);
    }
  };

  const handleSave = async (data) => {
    if (editingBox) {
      await api.patch(`/auth/boxes/${editingBox._id}`, data);
    } else {
      await api.post('/auth/boxes', data);
    }
    setShowForm(false);
    fetchBoxes();
  };

  const handleReorder = async (affectedItems) => {
    try {
      await api.patch('/auth/boxes/reorder', affectedItems);
      // Update local state optimistically
      const updatedBoxes = boxes.map(box => {
        const affected = affectedItems.find(item => item.id === box._id);
        if (affected) {
          return { ...box, displayOrder: affected.displayOrder };
        }
        return box;
      });
      setBoxes(updatedBoxes);
    } catch (err) {
      alert('Failed to reorder boxes');
      console.error(err);
      fetchBoxes(); // Revert to server state on error
    }
  };

  const formatDimensions = (dimensions) => {
    if (!dimensions) return '-';
    return `${dimensions.length} x ${dimensions.width} x ${dimensions.height} mm`;
  };

  const formatVolume = (volumeCM3) => {
    if (!volumeCM3) return '-';
    return `${volumeCM3.toLocaleString()} cm³`;
  };

  const formatCost = (costInCents) => {
    if (costInCents === undefined || costInCents === null) return '-';
    return `€${(costInCents / 100).toFixed(2)}`;
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'key', label: 'Key' },
    {
      key: 'dimensions',
      label: 'Dimensions (L x W x H)',
      render: (val) => formatDimensions(val)
    },
    {
      key: 'usableVolumeCM3',
      label: 'Usable Volume',
      render: (val) => formatVolume(val)
    },
    {
      key: 'emptyWeightGrams',
      label: 'Empty Weight',
      render: (val) => val ? `${val}g` : '-'
    },
    {
      key: 'unitCostInCents',
      label: 'Unit Cost',
      render: (val) => formatCost(val)
    },
    { key: 'displayOrder', label: 'Order' }
  ];

  const renderFilters = () => (
    <div className="flex flex-col sm:flex-row gap-3">
      <input
        type="text"
        placeholder="Search by name or key..."
        value={filters.search}
        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-slate-600">Loading boxes...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
      {error}
    </div>
  );

  return (
    <div>
      <PageHeader title="Boxes" description="Manage packaging box configurations" />
      {showForm ? (
        <BoxForm
          box={editingBox}
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
        />
      ) : (
        <DataTable
          data={filteredBoxes}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAdd={handleAdd}
          renderFilters={renderFilters}
          reorderable={true}
          onReorder={handleReorder}
        />
      )}
    </div>
  );
}
